import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { onboardingService, OnbDocTemplate } from '../services/onboardingService';
import { uid } from '../lib/onboardingDocsStore';
import { useAuth } from '../contexts/AuthContext';
import '../styles/CustomersList.css';
import '../styles/Pages.css';
import '../styles/InsightsPage.css';
import '../styles/OnboardingForms.css';

const fileToDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

const OnboardingDocuments: React.FC = () => {
  const navigate = useNavigate();
  const { currentOrg } = useAuth();
  const [search, setSearch] = useState('');
  const [templates, setTemplates] = useState<OnbDocTemplate[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!currentOrg?.id) return;
    onboardingService.listTemplates()
      .then(setTemplates)
      .catch(err => console.error('Failed to load document templates:', err));
  }, [currentOrg?.id]);

  const q = search.trim().toLowerCase();
  const filtered = useMemo(
    () => templates.filter(t => !q || t.name.toLowerCase().includes(q)),
    [templates, q],
  );

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      alert('Only PDF files are allowed.');
      return;
    }
    const dataUrl = await fileToDataUrl(file);
    try {
      const tpl = await onboardingService.createTemplate({
        name: file.name,
        description: '',
        pdfDataUrl: dataUrl,
        roles: [{ id: uid(), name: 'Client', color: '#3b82f6' }],
        fields: [],
      });
      navigate(`/onboarding/documents/${tpl.id}/edit`);
    } catch (err: any) {
      console.error('Failed to create template:', err);
      alert(`Failed to create template: ${err?.message ?? err}`);
    }
  };

  const removeTemplate = async (id: string) => {
    if (!window.confirm('Delete this document template?')) return;
    setTemplates(prev => prev.filter(t => t.id !== id));
    try { await onboardingService.deleteTemplate(id); }
    catch (err) { console.error('Failed to delete template:', err); }
  };

  return (
    <Layout>
      <div className="customers-list-container">
        <div className="customers-header-row">
          <h1 className="customers-title">Documents</h1>
          <div className="header-right-section">
            <div className="search-bar">
              <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
              <input type="text" placeholder="Search templates..." value={search} onChange={e => setSearch(e.target.value)} className="search-input" />
            </div>
            <input ref={fileRef} type="file" accept="application/pdf,.pdf" style={{ display: 'none' }} onChange={onPickFile} />
            <button className="customers-add-btn btn-primary-cta" title="Upload a PDF and create a template" onClick={() => fileRef.current?.click()}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
              Create Document Template
            </button>
          </div>
        </div>

        <div className="onbf-wrap">
          <div className="onbf-row onbf-head">
            <span className="onbf-col-name">Name</span>
            <span className="onbf-col-actions">Actions</span>
          </div>

          {filtered.map(t => (
            <div key={t.id} className="onbf-row">
              <span className="onbf-col-name onbf-name">
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                  {t.name}
                </span>
              </span>
              <span className="onbf-col-actions onbf-actions">
                <button type="button" className="onbf-action" title="Edit" onClick={() => navigate(`/onboarding/documents/${t.id}/edit`)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                </button>
                <button type="button" className="onbf-action onbf-action-danger" title="Delete" onClick={() => removeTemplate(t.id)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" /></svg>
                </button>
              </span>
            </div>
          ))}

          {filtered.length === 0 && (
            <div className="onbf-empty">No document templates yet. Click “Create Document Template” to upload a PDF.</div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default OnboardingDocuments;
